import React, { useState, useEffect, useRef } from 'react';
import {
  X,
  Upload,
  Download,
  Calendar,
  User,
  MapPin,
  Package,
  FileText,
  Banknote,
  Clock,
  AlertCircle,
  Camera,
  Eye,
  ChevronDown
} from 'lucide-react';
import { formatZAR } from '../utils/currency';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import SignatureSection from './service_logs/SignatureSection';
import AttachmentsSection from './service_logs/AttachmentsSection';

// Assets will be fetched from API based on selected property

// Mock data for technicians (replace with actual API call if available)
const mockTechnicians = [
  { name: 'John Doe', email: 'john.doe@example.com' },
  { name: 'Jane Smith', email: 'jane.smith@example.com' },
  { name: 'Peter Jones', email: 'peter.jones@example.com' },
];

const ServiceLogForm = ({
  log,
  onSave,
  onCancel,
  serviceTypes,
  statusTypes,
  priorityLevels = ['Low', 'Medium', 'High', 'Critical'] // Set default value here
}) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    propertyId: '',
    propertyName: '',
    assetId: '',
    assetName: '',
    serviceType: '',
    technician: '',
    technicianEmail: '',
    scheduledDate: '',
    completedDate: '',
    status: 'Scheduled',
    priority: 'Medium',
    description: '',
    notes: '',
    duration: '',
    cost: '',
    attachments: []
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [properties, setProperties] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [showTechnicianDropdown, setShowTechnicianDropdown] = useState(false);
  const [filteredTechnicians, setFilteredTechnicians] = useState(mockTechnicians);
  const technicianDropdownRef = useRef(null);
  const technicianInputRef = useRef(null);
  
  // Signature-related state
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [signatureRequirements, setSignatureRequirements] = useState(null);
  const [signatures, setSignatures] = useState([]);
  const [loadingSignatures, setLoadingSignatures] = useState(false);
  const [signingInProgress, setSigningInProgress] = useState(false);

  const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  // Get API configuration from localStorage
  const getApiConfig = () => {
    try {
      const token = localStorage.getItem('properties_api_token') || '';
      const endpoint = localStorage.getItem('properties_api_endpoint') || '';
      return { token, endpoint };
    } catch (error) {
      console.error('Error getting API config:', error);
      return { token: '', endpoint: '' };
    }
  };

  // Create axios instance with API token headers
  const createApiRequest = () => {
    const { token, endpoint } = getApiConfig();
    const headers = {};

    if (token) {
      headers['X-Properties-API-Token'] = token;
    }
    if (endpoint) {
      headers['X-Properties-API-Endpoint'] = endpoint;
    }

    return axios.create({
      baseURL: API,
      headers
    });
  };

  // Fetch assets for a specific property
  const fetchAssets = async (propertyId) => {
    if (!propertyId) {
      setAssets([]);
      return;
    }

    setLoadingAssets(true);
    try {
      const apiClient = createApiRequest();
      const response = await apiClient.get(`/api/properties/${propertyId}/assets`);
      console.log('Assets response:', response.data);
      setAssets(response.data.assets || []);
    } catch (error) {
      console.error('Failed to fetch assets:', error);
      setAssets([]);
    } finally {
      setLoadingAssets(false);
    }
  };

  // Fetch signature requirements for the service log
  const fetchSignatureRequirements = async (logId) => {
    if (!logId) return;
    
    try {
      const response = await fetch(`${API}/api/service-logs/${logId}/signature-requirements`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Signature requirements response:', data);
        console.log('User permissions:', data.user_permissions);
        setSignatureRequirements(data);
      } else {
        console.error('Failed to fetch signature requirements - HTTP status:', response.status);
      }
    } catch (error) {
      console.error('Failed to fetch signature requirements:', error);
    }
  };

  // Fetch existing signatures for the service log
  const fetchSignatures = async (logId) => {
    if (!logId) return;
    
    setLoadingSignatures(true);
    try {
      const response = await fetch(`${API}/api/service-logs/${logId}/signatures`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setSignatures(data.signatures || []);
      }
    } catch (error) {
      console.error('Failed to fetch signatures:', error);
      setSignatures([]);
    } finally {
      setLoadingSignatures(false);
    }
  };

  // Handle signature submission
  const handleSignatureSubmit = async (signatureData) => {
    if (!log?.id) return;
    
    setSigningInProgress(true);
    try {
      const response = await fetch(`${API}/api/service-logs/${log.id}/sign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(signatureData)
      });
      
      if (response.ok) {
        const result = await response.json();
        setShowSignatureModal(false);
        
        // Refresh signatures and requirements
        await Promise.all([
          fetchSignatures(log.id),
          fetchSignatureRequirements(log.id)
        ]);
        
        alert('Signature added successfully!');
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to add signature');
      }
    } catch (error) {
      console.error('Failed to submit signature:', error);
      alert(`Failed to add signature: ${error.message}`);
    } finally {
      setSigningInProgress(false);
    }
  };


  useEffect(() => {
    // Fetch properties from backend
    const fetchProperties = async () => {
      try {
        const apiClient = createApiRequest();
        const response = await apiClient.get('/api/properties/');
        setProperties(response.data);
      } catch (error) {
        console.error('Failed to fetch properties:', error);
        setProperties([]);
      }
    };

    fetchProperties();
  }, []);

  // Handle clicking outside technician dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (technicianDropdownRef.current && !technicianDropdownRef.current.contains(event.target)) {
        setShowTechnicianDropdown(false);
      }
    };

    if (showTechnicianDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showTechnicianDropdown]);

  useEffect(() => {
    if (log) {
      const propertyId = log.propertyId || log.property_id || '';
      setFormData({
        propertyId: propertyId,
        propertyName: log.propertyName || log.property_name || '',
        assetId: log.assetId || log.asset_id || '',
        assetName: log.assetName || log.asset_name || '',
        serviceType: log.serviceType || log.service_type || '',
        technician: log.technician || '',
        technicianEmail: log.technicianEmail || log.technician_email || '',
        scheduledDate: log.scheduledDate ?
          new Date(log.scheduledDate).toISOString().split('T')[0] :
          (log.scheduled_date ? new Date(log.scheduled_date).toISOString().split('T')[0] : ''),
        completedDate: log.completedDate ?
          new Date(log.completedDate).toISOString().split('T')[0] :
          (log.completed_date ? new Date(log.completed_date).toISOString().split('T')[0] : ''),
        status: log.status || 'Scheduled',
        priority: log.priority || 'Medium',
        description: log.description || '',
        notes: log.notes || '',
        duration: log.duration?.toString() || '',
        cost: log.cost?.toString() || '',
        attachments: (log.attachments || []).map(att => ({
          ...att,
          service_log_id: log.id
        }))
      });

      // If editing an existing log with a property, fetch assets for that property
      if (propertyId) {
        fetchAssets(propertyId);
      }
      
      // If editing an existing log, fetch signature information
      if (log.id) {
        fetchSignatureRequirements(log.id);
        fetchSignatures(log.id);
      }
    } else {
      if (user.role === 'Technician' || user.role === 'Maintenance') {
        setFormData(prev => ({
          ...prev,
          technician: `${user.name} ${user.surname}`,
          technicianEmail: user.email
        }));
      }
    }
  }, [log, user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }

    if (name === 'propertyId') {
      const property = properties.find(p => p.id === value);
      setFormData(prev => ({
        ...prev,
        propertyName: property?.building_name || property?.buildingName || '',
        assetId: '',
        assetName: ''
      }));
      // Fetch assets for the selected property
      fetchAssets(value);
    }

    if (name === 'assetId') {
      const asset = assets.find(a => a.id === value);
      setFormData(prev => ({ ...prev, assetName: asset?.name || asset?.asset_name || '' }));
    }

    if (name === 'technician') {
      const tech = mockTechnicians.find(t => t.name === value);
      setFormData(prev => ({
        ...prev,
        technicianEmail: tech?.email || ''
      }));
      
      // Filter technicians based on input
      const filtered = mockTechnicians.filter(t => 
        t.name.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredTechnicians(filtered);
      setShowTechnicianDropdown(value.length > 0 && filtered.length > 0);
    }

    if (name === 'status' && value === 'Completed' && !formData.completedDate) {
      setFormData(prev => ({
        ...prev,
        completedDate: new Date().toISOString().split('T')[0]
      }));
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);

    for (const file of files) {
      const fileId = Date.now() + Math.random();
      setUploadProgress(prev => ({ ...prev, [fileId]: 0 }));

      try {
        const uploadFormData = new FormData();
        uploadFormData.append('file', file);

        if (log && log.id) {
          const uploadResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/service-logs/${log.id}/attachments`, {
            method: 'POST',
            credentials: 'include',
            body: uploadFormData,
          });

          if (!uploadResponse.ok) {
            const errorData = await uploadResponse.json();
            throw new Error(errorData.detail || 'Upload failed');
          }

          const uploadResult = await uploadResponse.json();

          setFormData(prev => ({
            ...prev,
            attachments: [...prev.attachments, { ...uploadResult.attachment, service_log_id: log.id }]
          }));
        } else {
          const reader = new FileReader();
          reader.onload = () => {
            const attachment = {
              id: fileId,
              filename: file.name,
              content_type: file.type,
              size: file.size,
              file: file,
              temporary: true,
              uploaded_at: new Date().toISOString()
            };

            setFormData(prev => ({
              ...prev,
              attachments: [...prev.attachments, attachment]
            }));

            setUploadProgress(prev => ({ ...prev, [fileId]: 100 }));
          };
          reader.readAsDataURL(file);
        }
      } catch (error) {
        console.error('File upload error:', error);
        setErrors(prev => ({
          ...prev,
          attachments: `Failed to upload ${file.name}: ${error.message}`
        }));
      } finally {
        setTimeout(() => {
          setUploadProgress(prev => {
            const newProgress = { ...prev };
            delete newProgress[fileId];
            return newProgress;
          });
        }, 2000);
      }
    }
  };

  const handleRemoveAttachment = async (attachmentId) => {
    const attachment = formData.attachments.find(att => att.id === attachmentId);

    if (attachment && !attachment.temporary && log && log.id) {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/service-logs/${log.id}/attachments/${attachmentId}`,
          {
            method: 'DELETE',
            credentials: 'include',
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Failed to delete attachment');
        }
      } catch (error) {
        console.error('Failed to delete attachment:', error);
        alert(`Failed to delete attachment: ${error.message}`);
        return;
      }
    }

    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter(att => att.id !== attachmentId)
    }));
  };

  const handleDownloadAttachment = async (attachment) => {
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/service-logs/${attachment.service_log_id}/attachments/${attachment.id}`;
      const response = await fetch(url, { credentials: 'include' });

      if (!response.ok) {
        console.error(`Failed to download attachment: HTTP Status ${response.status} - ${response.statusText}`);
        let errorMessage = `Failed to download attachment: ${response.status} ${response.statusText}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorMessage;
        } catch (jsonError) {
          console.warn('Failed to parse error response as JSON:', jsonError);
        }
        throw new Error(errorMessage);
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = attachment.filename || 'download';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Download failed:', err);
      alert(`Failed to download file: ${err.message}`);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.propertyId) newErrors.propertyId = 'Property is required';
    if (!formData.serviceType) newErrors.serviceType = 'Service type is required';
    if (!formData.technician) newErrors.technician = 'Technician is required';
    if (!formData.scheduledDate) newErrors.scheduledDate = 'Scheduled date is required';
    if (!formData.description) newErrors.description = 'Description is required';

    if (formData.technicianEmail && !/\S+@\S+\.\S+/.test(formData.technicianEmail)) {
      newErrors.technicianEmail = 'Invalid email format';
    }

    if (formData.cost && isNaN(parseFloat(formData.cost))) {
      newErrors.cost = 'Cost must be a valid number';
    }

    if (formData.duration && isNaN(parseInt(formData.duration))) {
      newErrors.duration = 'Duration must be a valid number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const submitData = {
        ...formData,
        cost: formData.cost ? parseFloat(formData.cost) : null,
        duration: formData.duration ? parseInt(formData.duration) : null,
        attachments: formData.attachments.filter(att => !att.temporary)
      };

      const savedLog = await onSave(submitData);

      const temporaryFiles = formData.attachments.filter(att => att.temporary);
      if (savedLog && temporaryFiles.length > 0) {
        for (const tempFile of temporaryFiles) {
          try {
            const uploadFormData = new FormData();
            uploadFormData.append('file', tempFile.file);

            const uploadResponse = await fetch(
              `${process.env.REACT_APP_BACKEND_URL}/api/service-logs/${savedLog.id}/attachments`,
              {
                method: 'POST',
                credentials: 'include',
                body: uploadFormData,
              }
            );

            if (!uploadResponse.ok) {
              console.error(`Failed to upload ${tempFile.filename}`);
            } else {
              console.log(`Successfully uploaded ${tempFile.filename}`);
            }
          } catch (error) {
            console.error(`Error uploading ${tempFile.filename}:`, error);
          }
        }
      }

    } catch (error) {
      console.error('Error saving service log:', error);

      if (error.response?.data?.detail) {
        alert(`Failed to save service log: ${error.response.data.detail}`);
      } else {
        alert('Failed to save service log. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Assets are now fetched from API and stored in the assets state

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
          <h2 className="text-2xl font-bold text-gray-900">
            {log ? 'Edit Service Log' : 'Create Service Log'}
          </h2>
          <button
            onClick={onCancel}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X size={24} className="text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Property and Asset Selection */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <MapPin size={16} className="inline mr-1" />
                Property *
              </label>
              <select
                name="propertyId"
                value={formData.propertyId}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent ${errors.propertyId ? 'border-red-500' : 'border-gray-300'
                  }`}
                required
              >
                <option value="">Select Property</option>
                {properties.map(property => (
                  <option key={property.id} value={property.id}>
                    {property.building_name || property.buildingName}
                  </option>
                ))}
              </select>
              {errors.propertyId && (
                <p className="text-red-600 text-sm mt-1">{errors.propertyId}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Package size={16} className="inline mr-1" />
                Asset
              </label>
              <select
                name="assetId"
                value={formData.assetId}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                disabled={!formData.propertyId || loadingAssets}
              >
                <option value="">
                  {loadingAssets ? 'Loading assets...' : 'Select Asset (Optional)'}
                </option>
                {assets.map(asset => (
                  <option key={asset.id} value={asset.id}>
                    {asset.name || asset.asset_name}
                  </option>
                ))}
              </select>
              {loadingAssets && (
                <div className="flex items-center mt-2 text-sm text-gray-500">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-cyan-500 mr-2"></div>
                  Loading assets for selected property...
                </div>
              )}
            </div>
          </div>

          {/* Service Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FileText size={16} className="inline mr-1" />
                Service Type *
              </label>
              <select
                name="serviceType"
                value={formData.serviceType}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent ${errors.serviceType ? 'border-red-300' : 'border-gray-300'
                  }`}
                required
              >
                <option value="">Select Service Type</option>
                {serviceTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
              {errors.serviceType && (
                <p className="text-red-600 text-sm mt-1">{errors.serviceType}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <AlertCircle size={16} className="inline mr-1" />
                Priority
              </label>
              <select
                name="priority"
                value={formData.priority}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
              >
                {priorityLevels.map(priority => (
                  <option key={priority} value={priority}>
                    {priority}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Technician Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <User size={16} className="inline mr-1" />
                Technician *
              </label>
              <div className="relative" ref={technicianDropdownRef}>
                <input
                  ref={technicianInputRef}
                  type="text"
                  name="technician"
                  value={formData.technician}
                  onChange={handleChange}
                  onFocus={() => {
                    if (formData.technician.length > 0) {
                      setShowTechnicianDropdown(true);
                    }
                  }}
                  onBlur={(e) => {
                    // Delay hiding dropdown to allow for clicks
                    setTimeout(() => {
                      if (!technicianDropdownRef.current?.contains(document.activeElement)) {
                        setShowTechnicianDropdown(false);
                      }
                    }, 150);
                  }}
                  className={`w-full px-3 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent ${errors.technician ? 'border-red-300' : 'border-gray-300'
                    }`}
                  placeholder="Enter technician name"
                  required
                  autoComplete="off"
                />
                <ChevronDown 
                  size={16} 
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" 
                />
                
                {/* Custom Dropdown */}
                {showTechnicianDropdown && filteredTechnicians.length > 0 && (
                  <div 
                    className="absolute z-50 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto"
                    style={{
                      top: (() => {
                        if (!technicianInputRef.current) return '100%';
                        const rect = technicianInputRef.current.getBoundingClientRect();
                        const spaceBelow = window.innerHeight - rect.bottom;
                        const spaceAbove = rect.top;
                        const dropdownHeight = Math.min(192, filteredTechnicians.length * 48); // max-h-48 = 192px, each item ~48px
                        
                        // If not enough space below and more space above, position above
                        if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
                          return 'auto';
                        }
                        return '100%';
                      })()
                    }}
                    {...((() => {
                      if (!technicianInputRef.current) return {};
                      const rect = technicianInputRef.current.getBoundingClientRect();
                      const spaceBelow = window.innerHeight - rect.bottom;
                      const spaceAbove = rect.top;
                      const dropdownHeight = Math.min(192, filteredTechnicians.length * 48);
                      
                      if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
                        return { bottom: '100%', marginBottom: '4px' };
                      }
                      return { marginTop: '4px' };
                    })())}
                  >
                    {filteredTechnicians.map((tech, index) => (
                      <div
                        key={tech.email}
                        className="px-3 py-2 hover:bg-cyan-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
                        onMouseDown={(e) => {
                          e.preventDefault(); // Prevent input blur
                          setFormData(prev => ({
                            ...prev,
                            technician: tech.name,
                            technicianEmail: tech.email
                          }));
                          setShowTechnicianDropdown(false);
                          if (errors.technician) {
                            setErrors(prev => ({ ...prev, technician: '' }));
                          }
                        }}
                        onTouchStart={(e) => {
                          e.preventDefault();
                          setFormData(prev => ({
                            ...prev,
                            technician: tech.name,
                            technicianEmail: tech.email
                          }));
                          setShowTechnicianDropdown(false);
                          if (errors.technician) {
                            setErrors(prev => ({ ...prev, technician: '' }));
                          }
                        }}
                      >
                        <div className="font-medium text-gray-900">{tech.name}</div>
                        <div className="text-sm text-gray-500">{tech.email}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {errors.technician && (
                <p className="text-red-600 text-sm mt-1">{errors.technician}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Technician Email
              </label>
              <input
                type="email"
                name="technicianEmail"
                value={formData.technicianEmail}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent ${errors.technicianEmail ? 'border-red-300' : 'border-gray-300'
                  }`}
                placeholder="technician@company.com"
              />
              {errors.technicianEmail && (
                <p className="text-red-600 text-sm mt-1">{errors.technicianEmail}</p>
              )}
            </div>
          </div>

          {/* Dates and Status */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar size={16} className="inline mr-1" />
                Scheduled Date *
              </label>
              <input
                type="date"
                name="scheduledDate"
                value={formData.scheduledDate}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent ${errors.scheduledDate ? 'border-red-300' : 'border-gray-300'
                  }`}
                required
              />
              {errors.scheduledDate && (
                <p className="text-red-600 text-sm mt-1">{errors.scheduledDate}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Completed Date
              </label>
              <input
                type="date"
                name="completedDate"
                value={formData.completedDate}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
              >
                {statusTypes.map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Duration and Cost */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Clock size={16} className="inline mr-1" />
                Duration (minutes)
              </label>
              <input
                type="number"
                name="duration"
                value={formData.duration}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent ${errors.duration ? 'border-red-300' : 'border-gray-300'
                  }`}
                placeholder="120"
                min="0"
              />
              {errors.duration && (
                <p className="text-red-600 text-sm mt-1">{errors.duration}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Banknote size={16} className="inline mr-1" />
                Cost (ZAR)
              </label>
              <input
                type="number"
                name="cost"
                value={formData.cost}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent ${errors.cost ? 'border-red-300' : 'border-gray-300'
                  }`}
                placeholder="R 4,500.00"
                min="0"
                step="0.01"
              />
              {errors.cost && (
                <p className="text-red-600 text-sm mt-1">{errors.cost}</p>
              )}
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description *
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={3}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent resize-none ${errors.description ? 'border-red-300' : 'border-gray-300'
                }`}
              placeholder="Describe the service work to be performed..."
              required
            />
            {errors.description && (
              <p className="text-red-600 text-sm mt-1">{errors.description}</p>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent resize-none"
              placeholder="Additional notes, observations, or findings..."
            />
          </div>

          {/* Digital Signatures Section - extracted to SignatureSection component */}
          <SignatureSection
            log={log}
            signatureRequirements={signatureRequirements}
            signatures={signatures}
            loadingSignatures={loadingSignatures}
            signingInProgress={signingInProgress}
            showSignatureModal={showSignatureModal}
            setShowSignatureModal={setShowSignatureModal}
            onSignatureSubmit={handleSignatureSubmit}
            user={user}
          />

          {/* File Attachments - extracted to AttachmentsSection component */}
          <AttachmentsSection
            log={log}
            attachments={formData.attachments}
            uploadProgress={uploadProgress}
            onFileUpload={handleFileUpload}
            onDownloadAttachment={handleDownloadAttachment}
            onRemoveAttachment={handleRemoveAttachment}
          />

          {/* Form Actions */}
          <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || Object.keys(uploadProgress).length > 0}
              className="px-6 py-2 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: '#00C5CB' }}
            >
              {isSubmitting ? (
                <span className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {log ? 'Updating...' : 'Creating...'}
                </span>
              ) : (
                log ? 'Update Service Log' : 'Create Service Log'
              )}
            </button>
          </div>
        </form>
      </div>

    </div>
  );
};

export default ServiceLogForm;